import { useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PrivacyLink } from "@/components/privacy/PrivacyLink";
import { MobileTermsLink } from "@/components/privacy/MobileTermsLink";
import { CookieSettingsLink } from "@/components/privacy/CookieSettingsLink";

const INITIAL_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  firm: "",
  investor_type: "",
  check_size: "",
  note: "",
  email_opt_in: true,
  source: "invest-page",
};

const fieldClass = "w-full border-0 border-b border-white/25 bg-transparent px-0 py-3 text-base text-white outline-none placeholder:text-white/30 focus:border-[#a8c69f] focus:ring-0";

export const InvestorInquiry = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.investorInquiry(form);
      setSubmitted(true);
      toast.success("Your request has been received.");
    } catch (err) {
      setError("We couldn’t send your request. Please email the founders directly below.");
      toast.error("Unable to send your request.");
    } finally {
      setSubmitting(false);
    }
  };

  const mailto = `mailto:hello@takevitura.com?subject=${encodeURIComponent("Vitura investor conversation")}&body=${encodeURIComponent(`Hi Vitura team,\n\nI would like to schedule an investor conversation.\n\nName: ${form.first_name} ${form.last_name}\nFirm: ${form.firm}\n`)}`;

  return (
    <section id="investor-contact" className="bg-[#0a0b0a] text-white" data-testid="invest-contact">
      <div className="mx-auto grid max-w-site grid-cols-1 px-6 py-24 md:grid-cols-12 md:px-10 md:py-32">
        <div className="md:col-span-5 md:pr-12 lg:pr-20">
          <div className="label !text-[10px] !text-white/50">06 / Continue the conversation</div>
          <h2 className="mt-7 font-display text-4xl leading-[1.02] sm:text-5xl" data-testid="invest-contact-title">
            Ready to go<br /><span className="editorial font-normal">deeper?</span>
          </h2>
          <p className="mt-7 max-w-md text-[15px] leading-relaxed text-white/65">
            Founder conversations are scheduled personally. Share a few details and one of us will follow up directly with timing.
          </p>
          <div className="mt-10 border-t border-white/15 pt-6">
            <div className="label !text-[8.5px] !text-white/40">Prefer email</div>
            <a href="mailto:hello@takevitura.com?subject=Vitura%20investor%20conversation" className="mt-2 inline-flex items-center gap-2 text-sm text-white transition-opacity duration-200 hover:opacity-60" data-testid="invest-direct-email-link">
              hello@takevitura.com <ArrowUpRight size={14} />
            </a>
          </div>
        </div>

        <div className="mt-16 border-t border-white/20 pt-10 md:col-span-7 md:mt-0 md:border-l md:border-t-0 md:pl-12 md:pt-0 lg:pl-20">
          {submitted ? (
            <div className="flex min-h-[520px] flex-col justify-center" data-testid="invest-form-success">
              <span className="flex h-10 w-10 items-center justify-center border border-[#a8c69f] text-[#a8c69f]"><Check size={18} /></span>
              <h3 className="mt-7 font-display text-3xl">Request received.</h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/65">A Vitura founder will follow up directly. If timing is important, open an email now and we’ll prioritize it.</p>
              <a href={mailto} className="mt-8 inline-flex w-fit min-h-12 items-center gap-2 bg-white px-6 py-3 label !text-[9px] !text-black transition-colors duration-200 hover:bg-[#a8c69f]" data-testid="invest-success-email-link">
                Open email <ArrowUpRight size={14} />
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="ph-no-capture ph-mask" data-testid="invest-inquiry-form">
              <div className="grid grid-cols-1 gap-x-8 gap-y-7 sm:grid-cols-2">
                <label className="block text-xs text-white/55">First name
                  <input required name="first_name" value={form.first_name} onChange={update} className={fieldClass} data-testid="invest-first-name-input" />
                </label>
                <label className="block text-xs text-white/55">Last name
                  <input required name="last_name" value={form.last_name} onChange={update} className={fieldClass} data-testid="invest-last-name-input" />
                </label>
                <label className="block text-xs text-white/55">Email
                  <input required type="email" name="email" value={form.email} onChange={update} className={fieldClass} data-testid="invest-email-input" />
                </label>
                <label className="block text-xs text-white/55">Phone <span className="text-white/30">(optional)</span>
                  <input type="tel" name="phone" value={form.phone} onChange={update} className={fieldClass} data-testid="invest-phone-input" />
                </label>
                <label className="block text-xs text-white/55">Firm or company <span className="text-white/30">(optional)</span>
                  <input name="firm" value={form.firm} onChange={update} className={fieldClass} data-testid="invest-firm-input" />
                </label>
                <label className="block text-xs text-white/55">Investor type
                  <select name="investor_type" value={form.investor_type} onChange={update} className={fieldClass} data-testid="invest-type-select">
                    <option value="" className="text-black">Select</option>
                    <option value="Angel" className="text-black">Angel</option>
                    <option value="Family office" className="text-black">Family office</option>
                    <option value="Venture fund" className="text-black">Venture fund</option>
                    <option value="Strategic" className="text-black">Strategic</option>
                    <option value="Other" className="text-black">Other</option>
                  </select>
                </label>
                <label className="block text-xs text-white/55 sm:col-span-2">Typical investment size <span className="text-white/30">(optional)</span>
                  <select name="check_size" value={form.check_size} onChange={update} className={fieldClass} data-testid="invest-check-size-select">
                    <option value="" className="text-black">Select</option>
                    <option value="Under $25K" className="text-black">Under $25K</option>
                    <option value="$25K–$50K" className="text-black">$25K–$50K</option>
                    <option value="$50K–$100K" className="text-black">$50K–$100K</option>
                    <option value="$100K–$250K" className="text-black">$100K–$250K</option>
                    <option value="$250K+" className="text-black">$250K+</option>
                  </select>
                </label>
                <label className="block text-xs text-white/55 sm:col-span-2">What would you like to discuss? <span className="text-white/30">(optional)</span>
                  <textarea name="note" value={form.note} onChange={update} rows={3} className={`${fieldClass} resize-none`} data-testid="invest-note-input" />
                </label>
              </div>

              <label className="mt-7 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-white/55" data-testid="invest-consent-label">
                <input required type="checkbox" name="email_opt_in" checked={form.email_opt_in} onChange={update} className="mt-0.5 h-4 w-4 rounded-none accent-[#a8c69f]" data-testid="invest-consent-checkbox" />
                <span>I agree to receive investor-related updates from Vitura. I can unsubscribe at any time.</span>
              </label>

              <p className="mt-4 text-xs leading-relaxed text-white/55" data-testid="invest-privacy-notice">We use your details to respond to your inquiry and manage the communication preferences submitted here. See our <PrivacyLink testId="invest-form-privacy-link" newTab />.</p>

              {error && <p className="mt-5 border border-red-300/30 p-3 text-sm text-red-200" role="alert" data-testid="invest-form-error">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="mt-8 inline-flex min-h-12 items-center gap-3 bg-white px-6 py-3 label !text-[9px] !text-black transition-colors duration-200 hover:bg-[#a8c69f] disabled:cursor-wait disabled:opacity-50"
                data-testid="invest-submit-button"
              >
                {submitting ? "Sending request…" : "Schedule a founder call"} <ArrowUpRight size={14} />
              </button>
            </form>
          )}
        </div>
      </div>

      <footer className="border-t border-white/15" data-testid="invest-footer">
        <div className="mx-auto flex max-w-site flex-col gap-5 px-6 py-8 text-xs text-white/40 md:flex-row md:items-end md:justify-between md:px-10">
          <div>
            <img src="/assets/brand/Vitura-White.png" alt="Vitura" className="w-24 opacity-70" />
            <p className="mt-4 max-w-xl leading-relaxed">For discussion purposes only. This page does not constitute an offer to sell or a solicitation to buy securities. Any offering will be made only through definitive offering documents.</p>
          </div>
          <div className="flex flex-wrap items-center gap-6"><PrivacyLink testId="invest-footer-privacy-link" className="text-white/60" /><MobileTermsLink testId="invest-footer-mobile-terms-link" className="text-white/60" /><CookieSettingsLink testId="invest-footer-cookie-settings-link" className="text-white/60" /><a href="/" className="label !text-[8px] !text-white/55 transition-opacity duration-200 hover:opacity-60" data-testid="invest-return-home-link">Return to Vitura</a></div>
        </div>
      </footer>
    </section>
  );
};